import React from "react";
import SeoHead from "../components/SeoHead";

const PrivacyPolicyPage = () => {
    return (
        <>
        <SeoHead
            title="Tiply Privacy Policy"
            description="How Tiply OÜ collects, uses, protects, retains and shares personal data under the GDPR."
            canonicalPath="/privacy/"
            localizedAlternates={false}
            schemaType="WebPage"
        />
        <div className="max-w-4xl mx-auto px-4 py-10 text-gray-800">
            <h1 className="text-3xl font-bold mb-4">Tiply Privacy Policy</h1>
            <p className="text-sm text-gray-600 mb-8">Effective Date: 21.07.2025</p>

            <p className="mb-4">
                This Privacy Policy explains how Tiply OÜ ("Tiply," "we," "us," or "our") collects, uses, and protects your personal data in accordance with the General Data Protection Regulation (EU) 2016/679 ("GDPR").
            </p>
            <p className="mb-4">
                Tiply OÜ is registered in Estonia under company number 16652928, with its registered address at Juhkentali 8, Tallinn 10132, Estonia. If you have any questions about this Privacy Policy or wish to exercise your rights, please contact us at{" "}
                <a href="mailto:info@tiply.ee" className="text-blue-600 underline">info@tiply.ee</a>.
            </p>

            <Section title="1. Scope">
                <ul className="list-disc ml-5 space-y-1">
                    <li>Businesses that register with Tiply</li>
                    <li>Individuals who receive tips via Tiply</li>
                    <li>Customers who leave tips via our web-based interface</li>
                </ul>
            </Section>

            <Section title="2. What Data We Collect">
                <Subsection title="a) From Businesses">
                    <BulletList items={[
                        "Business name (legal and display)",
                        "Representative name and contact details",
                        "Business address",
                        "Business registration or tax identification number (where applicable)",
                        "Bank account details (IBAN)"
                    ]} />
                </Subsection>
                <Subsection title="b) From Individuals">
                    <BulletList items={[
                        "Full name",
                        "Profile photo (if provided)",
                        "IBAN (for receiving payouts)",
                        "Account login credentials (email, password)"
                    ]} />
                    <p className="mt-2">
                        In certain cases, we may request a copy of an identity document or proof of business registration if required by our banking partner, payment service provider, or applicable law.
                    </p>
                </Subsection>
                <Subsection title="c) From Tippers (Customers)">
                    <BulletList items={[
                        "Tip amount and timestamp",
                        "Technical data (device/browser info, IP address)"
                    ]} />
                    <p className="mt-2">
                        We do not collect or store any card or payment data. All payments are securely processed by Stripe or Montonio UAB.
                    </p>
                </Subsection>
            </Section>

            <Section title="3. How We Use Your Data">
                <BulletList items={[
                    "To register and manage Business and Individual accounts",
                    "To generate and manage QR codes for tip collection",
                    "To calculate and process tip payouts",
                    "To maintain payout history and reporting",
                    "To enable Individuals to access and manage their own tip data",
                    "To comply with legal obligations (e.g., financial and anti-fraud requirements)",
                    "To verify identity or business activity if required by our banking partner, payment service provider, or applicable law"
                ]} />
            </Section>

            <Section title="4. Legal Basis for Processing">
                <BulletList items={[
                    "Contractual necessity – to fulfill our obligations under the Tiply service agreement",
                    "Legal obligation – to comply with applicable laws",
                    "Legitimate interest – for platform integrity, user support, and service optimization"
                ]} />
            </Section>

            <Section title="5. Data Storage and Security">
                <p>
                    Personal data is stored securely in EU-based databases. Profile images and user data are stored in a cloud database (within the EU/EEA). We implement technical and organizational measures to prevent unauthorized access, loss, or misuse.
                </p>
                <p className="mt-2">
                    If we collect identity documents, they are stored securely with restricted access and deleted once they are no longer required for compliance purposes.
                </p>
            </Section>

            <Section title="6. Data Retention">
                <BulletList items={[
                    "Account data: retained while active and up to 6 years after closure",
                    "Transaction data: retained for up to 6 years",
                    "Profile photos: deleted upon account deletion",
                    "Identity documents (if collected): retained only as long as necessary to satisfy legal or banking partner requirements, then securely deleted"
                ]} />
            </Section>

            <Section title="7. Data Sharing">
                <BulletList items={[
                    "Stripe or Montonio UAB for processing tip payments",
                    "Legal/regulatory authorities if required by law",
                    "Cloud providers (within EU/EEA) for hosting and storage",
                    "Identity verification providers (e.g., Veriff) if we use third parties to perform document checks"
                ]} />
                <p className="mt-2">
                    We do not share data with advertisers, analytics tools, or third-party marketers.
                </p>
            </Section>

            <Section title="8. Your Rights">
                <p>Under GDPR, you have the right to:</p>
                <BulletList items={[
                    "Access your personal data",
                    "Correct inaccurate data",
                    "Request erasure (right to be forgotten)",
                    "Restrict processing",
                    "Object to processing",
                    "Data portability",
                    "Withdraw consent (where applicable)"
                ]} />
                <p className="mt-2">
                    To exercise any of these rights, contact us at{" "}
                    <a href="mailto:info@tiply.ee" className="text-blue-600 underline">info@tiply.ee</a>.
                </p>
            </Section>

            <Section title="9. Children">
                <p>
                    Our platform is not intended for individuals under 18. We do not knowingly collect data from minors. If a minor submits data, contact us to request deletion.
                </p>
            </Section>

            <Section title="10. Updates">
                <p>
                    We may update this Privacy Policy from time to time. Major changes will be posted on our website. Continued use of the platform means you accept the updated policy.
                </p>
            </Section>

            <Section title="11. Contact">
                <p>
                    Tiply OÜ<br />
                    Juhkentali 8, Tallinn 10132, Estonia<br />
                    Email:{" "}
                    <a href="mailto:info@tiply.ee" className="text-blue-600 underline">info@tiply.ee</a>
                </p>
            </Section>
        </div>
        </>
    );
};

const Section = ({ title, children }) => (
    <div className="mb-8">
        <h2 className="text-xl font-semibold mb-2">{title}</h2>
        {children}
    </div>
);

const Subsection = ({ title, children }) => (
    <div className="mb-4">
        <h3 className="font-medium">{title}</h3>
        {children}
    </div>
);

const BulletList = ({ items }) => (
    <ul className="list-disc ml-6 mt-2 space-y-1">
        {items.map((item, i) => <li key={i}>{item}</li>)}
    </ul>
);

export default PrivacyPolicyPage;
